import { Avatar, Button, Col, Input, Row, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import { useEffect, useRef, useState } from 'react';
import { TableComponents } from 'screens/Admin/components';

const COPY = {
  followers: {
    empty: 'Nobody follows this team yet.',
    search: 'Search followers...'
  },
  subscribers: {
    empty: 'No active subscribers yet.',
    search: 'Search subscribers...'
  }
};

let timer;

// Who follows, or holds an active subscription to, a team. Read-only: managing a fan happens on
// the Users tab, so a row here is a name to find, not a control.
const TeamAudiencePanel = ({ team, kind }) => {
  const [users, setUsers] = useState(null),
    [total, setTotal] = useState(0),
    [text, setText] = useState(''),
    [loadingMore, setLoadingMore] = useState(false),
    // The newest request wins: a slow answer to an earlier search must not overwrite a later one.
    requestId = useRef(0);

  useEffect(() => {
    clearTimeout(timer);
    timer = setTimeout(() => load(true), 300);

    return () => clearTimeout(timer);
  }, [team?._id, kind, text]);

  async function load(replace) {
    const id = ++requestId.current;

    if (replace) setUsers(null);
    else setLoadingMore(true);

    const res = await Api.post('/admin/getTeamAudience', {
      teamId: team._id,
      kind,
      text: text || undefined,
      skip: replace ? 0 : users.length
    });

    if (id !== requestId.current) return;

    setLoadingMore(false);
    if (!res) return setUsers((prev) => prev || []);

    setTotal(res.total);
    setUsers((prev) => (replace ? res.users : [...prev, ...res.users]));
  }

  if (!team?._id) return null;

  return (
    <Col noFlex pad="24px">
      <Row between center noFlex wid="100%" marg="0 0 16px">
        <Input
          big
          search
          rounded
          pad="0 24px"
          wid="360px"
          placeholder={COPY[kind].search}
          value={text}
          onChange={({ target }) => setText(target.value)}
        />

        {users && (
          <Text med col={theme.GREY_60}>
            {total} {total === 1 ? kind.slice(0, -1) : kind}
          </Text>
        )}
      </Row>

      {!users ? (
        <Col centerAll pad="48px 0">
          <Spinner2 col={theme.SECONDARY} />
        </Col>
      ) : !users.length ? (
        <Text col={theme.GREY_60} pad="24px 0">
          {text ? 'No one matches that search.' : COPY[kind].empty}
        </Text>
      ) : (
        <>
          <TableComponents.List
            Header={() => (
              <>
                <TableComponents.Th first>
                  <Text>Avatar</Text>
                </TableComponents.Th>
                <TableComponents.Th>
                  <Text>Name</Text>
                </TableComponents.Th>
                <TableComponents.Th>
                  <Text>Email</Text>
                </TableComponents.Th>
                <TableComponents.Th last>
                  <Text>Email verified</Text>
                </TableComponents.Th>
              </>
            )}
            Items={() =>
              users.map((user) => (
                <TableComponents.Tr key={user._id}>
                  <TableComponents.Td>
                    <Avatar user={user} avatar={user.avatar} tiny />
                  </TableComponents.Td>
                  <TableComponents.Td>
                    <Text>{user.name}</Text>
                    <Text c1 col={theme.GREY_60}>
                      @{user.username}
                    </Text>
                  </TableComponents.Td>
                  <TableComponents.Td>
                    <Text>{user.email?.includes('@') ? user.email : ''}</Text>
                  </TableComponents.Td>
                  <TableComponents.Td>
                    <Text>{user.verified?.email ? 'Verified' : 'Not verified'}</Text>
                  </TableComponents.Td>
                </TableComponents.Tr>
              ))
            }
          />

          {users.length < total && (
            <Row centerAll noFlex marg="16px 0 0">
              <Button
                small
                bordered
                wid="140px"
                text={loadingMore ? 'Loading...' : 'Show more'}
                disabled={loadingMore}
                onClick={() => load(false)}
              />
            </Row>
          )}
        </>
      )}
    </Col>
  );
};

export default TeamAudiencePanel;
